import React from 'react';
import {
  Check,
  Flame,
  Snowflake,
  Trash2,
  Undo2,
} from 'lucide-react';

const TONE_STYLES = Object.freeze({
  warm: {
    card: 'border-amber-300/45 bg-amber-400/[0.12] text-amber-50 hover:border-amber-200/70 hover:bg-amber-400/[0.18]',
    icon: 'border-amber-300/35 bg-amber-300/15 text-amber-200',
    label: 'text-amber-200',
  },
  cool: {
    card: 'border-sky-300/45 bg-sky-400/[0.12] text-sky-50 hover:border-sky-200/70 hover:bg-sky-400/[0.18]',
    icon: 'border-sky-300/35 bg-sky-300/15 text-sky-200',
    label: 'text-sky-200',
  },
  neutral: {
    card: 'border-cyan-200/15 bg-cyan-300/[0.08] text-cyan-50 hover:border-cyan-100/35 hover:bg-cyan-300/[0.14]',
    icon: 'border-cyan-200/20 bg-cyan-300/10 text-cyan-100',
    label: 'text-cyan-100/65',
  },
});

export const SHAPE_GLYPHS = Object.freeze({
  circle: '●',
  square: '■',
  triangle: '▲',
  diamond: '◆',
  oval: '⬭',
  pill: '▰',
  hex: '⬢',
  ring: '◌',
});

function getToneKey(card) {
  return card?.toneKey || card?.colorKey || 'neutral';
}

function getToneStyle(card) {
  return TONE_STYLES[getToneKey(card)] || TONE_STYLES.neutral;
}

function isRedundantTraitToken(token, card, glyph) {
  const normalized = String(token || '').trim().toLocaleLowerCase();
  if (!normalized) {
    return true;
  }
  if (normalized === String(glyph || '').trim().toLocaleLowerCase()) {
    return true;
  }
  const traitTerms = [
    card?.toneLabel,
    card?.displayToneLabel,
    card?.shapeGroupLabel,
    card?.toneKey,
    card?.shapeGroup,
    'warm',
    'cool',
    'caldo',
    'freddo',
    'cálido',
    'frío',
    'rounded form',
    'angular form',
    'forma arrotondata',
    'forma angolare',
    'forma redondeada',
    'forma angular',
  ].filter(Boolean).map((value) => String(value).trim().toLocaleLowerCase());
  return traitTerms.some((term) => normalized === term);
}

export function getCardGlyph(card) {
  return card?.glyph || SHAPE_GLYPHS[card?.symbolType] || SHAPE_GLYPHS[card?.sourceShapeId] || '';
}

function ToneIcon({ toneKey, className = 'h-3.5 w-3.5' }) {
  return toneKey === 'warm'
    ? <Flame className={className} aria-hidden="true" />
    : <Snowflake className={className} aria-hidden="true" />;
}

function VisualTraitCard({
  card,
  onClick,
  selected,
  disabled,
  status,
  modeShift = false,
}) {
  const toneKey = getToneKey(card);
  const canonicalShapeId = card?.sourceShapeId || card?.symbolType;
  const glyph = modeShift
    ? getCardGlyph(card)
    : SHAPE_GLYPHS[canonicalShapeId] || getCardGlyph(card);
  const printedToneLabel = card?.displayToneLabel || card?.toneLabel || '';
  const isWarm = toneKey === 'warm';
  const modeTraits = Array.isArray(card?.metaTokens)
    ? card.metaTokens.filter((token) => !isRedundantTraitToken(token, card, glyph))
    : [];
  const accessibleLabel = modeShift
    ? [
      card?.accessibilityLabel || card?.label,
      ...modeTraits,
    ].filter(Boolean).join(', ')
    : card?.accessibilityLabel || card?.label;
  const textureStyle = isWarm
    ? {
      backgroundImage: 'repeating-linear-gradient(135deg, rgba(255,255,255,0.09) 0 2px, transparent 2px 11px), linear-gradient(145deg, rgba(244,63,94,0.32), rgba(127,29,29,0.34))',
    }
    : {
      backgroundImage: 'radial-gradient(circle at 2px 2px, rgba(255,255,255,0.16) 1.4px, transparent 1.6px), linear-gradient(145deg, rgba(14,165,233,0.3), rgba(12,74,110,0.36))',
      backgroundSize: '12px 12px, auto',
    };
  const stateClass = status === 'incorrect'
    ? 'border-red-200/80 bg-red-500/14 ring-2 ring-red-400/75'
    : selected
      ? 'border-cyan-100 ring-2 ring-cyan-100 ring-offset-2 ring-offset-[#07121A]'
      : isWarm
        ? 'border-rose-300/55 hover:border-rose-200/80'
        : 'border-sky-300/55 hover:border-sky-200/80';

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-pressed={selected}
      aria-label={accessibleLabel}
      style={textureStyle}
      className={`relative flex min-h-36 flex-col items-center justify-center rounded-2xl border px-4 py-5 text-center text-white shadow-[0_18px_45px_rgba(0,0,0,0.22)] transition hover:brightness-110 focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-100 disabled:cursor-not-allowed disabled:opacity-45 ${stateClass}`}
    >
      {selected ? (
        <span className="absolute right-3 top-3 flex h-6 w-6 items-center justify-center rounded-full bg-cyan-100 text-slate-950">
          <Check className="h-4 w-4" aria-hidden="true" />
        </span>
      ) : null}
      <span
        className={`flex min-h-16 items-center justify-center leading-none drop-shadow-[0_4px_12px_rgba(0,0,0,0.25)] ${
          modeShift && card?.sizeKey !== 'large' ? 'text-4xl' : 'text-6xl'
        }`}
        aria-hidden="true"
      >
        {glyph}
      </span>
      <span className="mt-4 text-sm font-black uppercase tracking-[0.2em] drop-shadow-md">
        {printedToneLabel}
      </span>
    </button>
  );
}

export function SectionHeading({ title, supporting, action }) {
  return (
    <div className="mb-3 flex min-h-8 items-end justify-between gap-4">
      <div>
        <p className="text-[11px] font-black uppercase tracking-[0.2em] text-cyan-200">
          {title}
        </p>
        {supporting ? <p className="mt-1 text-sm leading-5 text-slate-400">{supporting}</p> : null}
      </div>
      {action}
    </div>
  );
}

export function UtilityActions({
  onUndo,
  onClear,
  undoDisabled,
  clearDisabled,
  undoLabel,
  clearLabel,
}) {
  return (
    <div className="flex gap-2">
      {onUndo ? (
        <button
          type="button"
          onClick={onUndo}
          disabled={undoDisabled}
          className="inline-flex min-h-11 items-center gap-2 rounded-xl border border-white/10 bg-slate-950/45 px-3 py-2 text-xs font-bold text-slate-300 transition hover:border-white/25 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-200 disabled:cursor-not-allowed disabled:opacity-35"
        >
          <Undo2 className="h-4 w-4" aria-hidden="true" />
          {undoLabel}
        </button>
      ) : null}
      <button
        type="button"
        onClick={onClear}
        disabled={clearDisabled}
        className="inline-flex min-h-11 items-center gap-2 rounded-xl border border-white/10 bg-slate-950/45 px-3 py-2 text-xs font-bold text-slate-300 transition hover:border-white/25 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-200 disabled:cursor-not-allowed disabled:opacity-35"
      >
        <Trash2 className="h-4 w-4" aria-hidden="true" />
        {clearLabel}
      </button>
    </div>
  );
}

export function MindOptionCard({
  card,
  onClick,
  selected,
  disabled,
  status = 'default',
  sequenceNumber,
  compact = false,
  presentation = 'default',
}) {
  if (presentation === 'focus-filter' || presentation === 'mode-shift') {
    return (
      <VisualTraitCard
        card={card}
        onClick={onClick}
        selected={selected}
        disabled={disabled}
        status={status}
        modeShift={presentation === 'mode-shift'}
      />
    );
  }

  const toneKey = getToneKey(card);
  const toneStyle = getToneStyle(card);
  const glyph = getCardGlyph(card);
  const trueToneLabel = card?.toneLabel || '';
  const printedToneLabel = card?.displayToneLabel || trueToneLabel;
  const hasTone = toneKey === 'warm' || toneKey === 'cool';
  const hasPrintedConflict = hasTone
    && printedToneLabel
    && trueToneLabel
    && printedToneLabel.toLocaleLowerCase() !== trueToneLabel.toLocaleLowerCase();
  const meta = (Array.isArray(card?.metaTokens)
    ? card.metaTokens.filter((token) => !isRedundantTraitToken(token, card, glyph))
    : [card?.angleLabel]
  ).filter(Boolean);
  const accessibleLabel = [
    card?.label,
    trueToneLabel ? `${trueToneLabel} tone` : '',
    printedToneLabel ? `printed word ${printedToneLabel}` : '',
    card?.shapeGroupLabel,
    ...meta,
  ].filter(Boolean).join(', ');
  const stateClass = status === 'incorrect'
    ? 'border-red-300/50 bg-red-500/14 text-red-50 hover:border-red-200/65 hover:bg-red-500/18'
    : selected
      ? `${toneStyle.card} bg-cyan-300/18 ring-2 ring-cyan-200/70 ring-offset-2 ring-offset-[#07121A]`
      : toneStyle.card;

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-pressed={selected}
      aria-label={card?.accessibilityLabel || accessibleLabel || card?.label}
      className={`relative min-h-24 rounded-2xl border text-left shadow-[0_16px_40px_rgba(0,0,0,0.14)] transition focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-100 disabled:cursor-not-allowed disabled:opacity-45 ${
        compact ? 'px-3 py-3' : 'px-4 py-4'
      } ${stateClass}`}
    >
      {sequenceNumber ? (
        <span className="absolute right-2.5 top-2.5 flex h-6 min-w-6 items-center justify-center rounded-full border border-white/15 bg-slate-950/55 px-1.5 text-[10px] font-black text-white">
          {sequenceNumber}
        </span>
      ) : null}
      {selected ? (
        <span className="absolute left-2.5 top-2.5 flex h-5 w-5 items-center justify-center rounded-full bg-cyan-200 text-slate-950">
          <Check className="h-3.5 w-3.5" aria-hidden="true" />
        </span>
      ) : null}
      <div className={`flex items-center gap-3 ${selected ? 'pt-5' : ''}`}>
        {Number.isFinite(Number(card?.rotationDegrees)) ? (
          <span
            aria-hidden="true"
            className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-cyan-200/20 bg-slate-950/45 text-3xl text-cyan-200"
            style={{ transform: `rotate(${Number(card.rotationDegrees)}deg)` }}
          >
            {glyph}
          </span>
        ) : glyph ? (
          <span
            aria-hidden="true"
            className={`inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border text-3xl ${toneStyle.icon} ${
              card?.sizeKey === 'large' ? 'scale-110' : ''
            } ${card?.fillKey === 'outlined' ? 'opacity-85' : ''}`}
          >
            {glyph}
          </span>
        ) : null}
        <span className="min-w-0 pr-5 text-sm font-black leading-5">{card?.label}</span>
      </div>
      {hasTone ? (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <span className={`inline-flex items-center gap-1.5 rounded-full border border-current/20 bg-slate-950/35 px-2 py-1 text-[10px] font-black uppercase tracking-[0.12em] ${toneStyle.label}`}>
            <ToneIcon toneKey={toneKey} />
            {trueToneLabel}
          </span>
          {printedToneLabel ? (
            <span className={`text-[10px] font-black uppercase tracking-[0.12em] ${
              hasPrintedConflict ? 'text-white' : 'text-slate-300'
            }`}>
              {printedToneLabel}
            </span>
          ) : null}
        </div>
      ) : null}
      {(card?.shapeGroupLabel || meta.length > 0) ? (
        <div className="mt-2 text-[10px] font-bold uppercase tracking-[0.12em] text-slate-300/80">
          {[card?.shapeGroupLabel, ...meta].filter(Boolean).join(' · ')}
        </div>
      ) : null}
    </button>
  );
}

export function MindPreviewCard({ card, index }) {
  const toneStyle = getToneStyle(card);
  const glyph = getCardGlyph(card);
  const hasTone = card?.toneKey === 'warm' || card?.toneKey === 'cool';

  return (
    <div className={`relative min-h-28 rounded-2xl border px-3 py-3 ${toneStyle.card}`}>
      <span className="absolute right-2.5 top-2.5 flex h-6 min-w-6 items-center justify-center rounded-full border border-white/15 bg-slate-950/55 px-1.5 text-[10px] font-black text-white">
        {index + 1}
      </span>
      <div className="flex min-h-12 items-center gap-2 pr-7">
        {glyph ? (
          <span className={`inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border text-2xl ${toneStyle.icon}`} aria-hidden="true">
            {glyph}
          </span>
        ) : null}
        <span className="text-sm font-black leading-5">{card?.label}</span>
      </div>
      {hasTone ? (
        <div className="mt-2 flex items-center gap-1.5 text-[10px] font-black uppercase tracking-[0.12em]">
          <ToneIcon toneKey={card.toneKey} />
          <span>{card.toneLabel}</span>
        </div>
      ) : null}
      {(card?.shapeGroupLabel || card?.metaLabel) ? (
        <p className="mt-1 text-[10px] font-bold uppercase tracking-[0.12em] text-slate-300">
          {card.shapeGroupLabel || card.metaLabel}
        </p>
      ) : null}
    </div>
  );
}

export function ResponseSlots({ cards, count, emptyLabel }) {
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-7">
      {Array.from({ length: count }, (_, index) => {
        const card = cards[index];
        const glyph = getCardGlyph(card);
        return (
          <div
            key={`mind-response-slot-${index + 1}`}
            className={`relative flex min-h-24 flex-col items-center justify-center rounded-2xl border px-3 py-4 text-center ${
              card
                ? 'border-cyan-200/40 bg-cyan-300/[0.1] text-white'
                : 'border-dashed border-white/20 bg-slate-950/35 text-slate-500'
            }`}
          >
            <span className="absolute -top-2.5 left-1/2 flex h-6 min-w-6 -translate-x-1/2 items-center justify-center rounded-full border border-cyan-200/20 bg-[#0B1A24] px-1.5 text-[10px] font-black text-cyan-100">
              {index + 1}
            </span>
            {card ? (
              <>
                {Number.isFinite(Number(card?.rotationDegrees)) ? (
                  <span
                    className="text-2xl text-cyan-200"
                    style={{ transform: `rotate(${Number(card.rotationDegrees)}deg)` }}
                    aria-hidden="true"
                  >
                    {glyph}
                  </span>
                ) : glyph ? <span className="text-2xl" aria-hidden="true">{glyph}</span> : null}
                <span className="mt-1 text-xs font-black">{card.label}</span>
              </>
            ) : (
              <span className="max-w-20 text-xs font-semibold leading-4">{emptyLabel}</span>
            )}
          </div>
        );
      })}
    </div>
  );
}

export function StageProgress({ stages = [], activeIndex = 0, label }) {
  return (
    <div className="flex items-center gap-3">
      <span className="text-[10px] font-black uppercase tracking-[0.16em] text-slate-400">{label}</span>
      <div className="flex items-center gap-1.5">
        {stages.map((stage, index) => (
          <React.Fragment key={stage.id || index}>
            <span className={`flex h-7 w-7 items-center justify-center rounded-full border text-[10px] font-black ${
              index < activeIndex
                ? 'border-emerald-300/40 bg-emerald-400/20 text-emerald-200'
                : index === activeIndex
                  ? 'border-cyan-200/70 bg-cyan-300/15 text-cyan-100'
                  : 'border-white/15 bg-slate-950/30 text-slate-500'
            }`}>
              {index < activeIndex ? <Check className="h-3.5 w-3.5" aria-hidden="true" /> : index + 1}
            </span>
            {index < stages.length - 1 ? <span className="h-px w-3 bg-white/15" /> : null}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}
